import React, { Dispatch } from 'react';
import { money } from '../format';
import {
  BUDGET_STEP,
  FurnishOption,
  LocationMode,
  MAX_BATHROOMS,
  MAX_BUDGET,
  WizardAction,
  WizardState,
  WizardStep,
} from './wizardState';

type StepProps = {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
};

const BEDROOMS = ['Studio', '1', '2', '3', '4', '5'];
const BUDGET_PRESETS: [number, number, string][] = [
  [0, 1500, 'Under £1,500'],
  [1500, 2500, '£1.5k – 2.5k'],
  [2500, 3500, '£2.5k – 3.5k'],
  [3500, 5000, '£3.5k – 5k'],
  [5000, MAX_BUDGET, '£5k+'],
];
const FURNISH: [FurnishOption, string][] = [
  ['furnished', 'Furnished'],
  ['part_furnished', 'Part-furnished'],
  ['unfurnished', 'Unfurnished'],
];
const SIZES = [0, 40, 50, 60, 75, 90, 110];
const DEPOSITS = [0, 2000, 3000, 4000, 6000, 8000];

function Chip({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" className="ma-chip" aria-pressed={pressed} onClick={onClick}>
      {children}
    </button>
  );
}

export function bedroomsRangeLabel(min: number, max: number): string {
  const word = (i: number) => (i === 0 ? 'Studio' : `${BEDROOMS[i]} bed`);
  return min === max ? word(min) : `${word(min)} – ${word(max)}`;
}

export function budgetRangeLabel(min: number, max: number): string {
  return `${money(min)} – ${max >= MAX_BUDGET ? '£10,000+' : money(max)}`;
}

export function bathroomsRangeLabel(min: number, max: number): string {
  if (min === 1 && max === MAX_BATHROOMS) return 'Any';
  return min === max ? String(min) : `${min} – ${max}`;
}

export function IntroStep() {
  return (
    <div>
      <h1 className="ma-title">Let's set up your flat search</h1>
      <p className="ma-subtitle">
        Answer a few quick questions and we'll send new listings from Rightmove, Zoopla and OpenRent straight to this chat.
      </p>
      <ol className="ma-steps-list">
        <li><b>1</b>Where you want to live</li>
        <li><b>2</b>Bedrooms and budget</li>
        <li><b>3</b>Optional must-haves</li>
      </ol>
      <p className="ma-hint">Takes about a minute. You can change everything later.</p>
    </div>
  );
}

const MODES: { id: LocationMode; title: string; desc: string; icon: string }[] = [
  { id: 'area', title: 'By neighbourhood', desc: 'Pick areas like Hackney or Clapham', icon: '⌂' },
  { id: 'postcode', title: 'By postcode', desc: 'Choose districts like N8 or SW4', icon: '#' },
];

export function ModeStep({ state, dispatch }: StepProps) {
  return (
    <div>
      <h1 className="ma-title">How would you like to search?</h1>
      <p className="ma-subtitle">Pick the one that fits how you think about London.</p>
      {MODES.map(mode => (
        <button
          key={mode.id}
          type="button"
          className="ma-option"
          aria-pressed={state.mode === mode.id}
          onClick={() => dispatch({ type: 'pickMode', mode: mode.id })}
        >
          <span className="ma-option__icon" aria-hidden>{mode.icon}</span>
          <span>
            <span className="ma-option__title">{mode.title}</span>
            <br />
            <span className="ma-option__desc">{mode.desc}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

export function BudgetStep({ state, dispatch }: StepProps) {
  return (
    <div className="ma-stack">
      <div>
        <h1 className="ma-title">Bedrooms and budget</h1>
        <p className="ma-subtitle">Set the ranges you'd consider.</p>
      </div>
      <section className="ma-card">
        <div className="ma-row">
          <h2 className="ma-section-title">Bedrooms</h2>
          <span className="ma-facts">{bedroomsRangeLabel(state.bedMin, state.bedMax)}</span>
        </div>
        <div className="ma-segment">
          {BEDROOMS.map((label, i) => (
            <Chip
              key={label}
              pressed={i >= state.bedMin && i <= state.bedMax}
              onClick={() => dispatch({ type: 'tapRange', field: 'bed', value: i })}
            >
              {label}
            </Chip>
          ))}
        </div>
      </section>
      <section className="ma-card">
        <div className="ma-row">
          <h2 className="ma-section-title">Monthly budget</h2>
          <span className="ma-facts">{budgetRangeLabel(state.budMin, state.budMax)}</span>
        </div>
        <label className="ma-hint">
          Minimum {money(state.budMin)}
          <input
            className="ma-range"
            type="range"
            min={0}
            max={MAX_BUDGET}
            step={BUDGET_STEP}
            value={state.budMin}
            onChange={e => dispatch({ type: 'setBudgetMin', value: Number(e.target.value) })}
          />
        </label>
        <label className="ma-hint">
          Maximum {state.budMax >= MAX_BUDGET ? '£10,000+' : money(state.budMax)}
          <input
            className="ma-range"
            type="range"
            min={0}
            max={MAX_BUDGET}
            step={BUDGET_STEP}
            value={state.budMax}
            onChange={e => dispatch({ type: 'setBudgetMax', value: Number(e.target.value) })}
          />
        </label>
        <p className="ma-section-title" style={{ margin: '12px 0 8px' }}>Quick picks</p>
        <div className="ma-chips">
          {BUDGET_PRESETS.map(([min, max, label]) => (
            <Chip
              key={label}
              pressed={state.budMin === min && state.budMax === max}
              onClick={() => dispatch({ type: 'setBudget', min, max })}
            >
              {label}
            </Chip>
          ))}
        </div>
      </section>
    </div>
  );
}

export function ExtrasStep({ state, dispatch }: StepProps) {
  return (
    <div className="ma-stack">
      <div>
        <h1 className="ma-title">Any must-haves?</h1>
        <p className="ma-subtitle">All optional. Skip anything you're flexible on.</p>
      </div>
      <section className="ma-card">
        <h2 className="ma-section-title" style={{ marginBottom: 10 }}>Move-in date</h2>
        <div className="ma-chips">
          <Chip pressed={state.anyDate} onClick={() => dispatch({ type: 'setAnyDate', anyDate: true })}>Any time</Chip>
          <Chip pressed={!state.anyDate} onClick={() => dispatch({ type: 'setAnyDate', anyDate: false })}>Pick a date</Chip>
        </div>
        {!state.anyDate && (
          <input
            className="ma-input"
            style={{ marginTop: 10 }}
            type="date"
            aria-label="Desired let available date"
            value={state.date}
            onChange={e => dispatch({ type: 'setDate', date: e.target.value })}
          />
        )}
        <p className="ma-hint">We match listings within ±10 days. Ads without a date still come through.</p>
      </section>
      <section className="ma-card">
        <h2 className="ma-section-title" style={{ marginBottom: 10 }}>Furnishing</h2>
        <div className="ma-chips">
          {FURNISH.map(([option, label]) => (
            <Chip
              key={option}
              pressed={state.furnish.includes(option)}
              onClick={() => dispatch({ type: 'toggleFurnish', option })}
            >
              {label}
            </Chip>
          ))}
        </div>
        <p className="ma-hint">Select all you're comfortable with. None selected means any.</p>
      </section>
      <section className="ma-card">
        <div className="ma-row">
          <h2 className="ma-section-title">Bathrooms</h2>
          <span className="ma-facts">{bathroomsRangeLabel(state.bathMin, state.bathMax)}</span>
        </div>
        <div className="ma-segment ma-segment--5">
          {[1, 2, 3, 4, 5].map(n => (
            <Chip
              key={n}
              pressed={n >= state.bathMin && n <= state.bathMax}
              onClick={() => dispatch({ type: 'tapRange', field: 'bath', value: n })}
            >
              {n}
            </Chip>
          ))}
        </div>
      </section>
      <section className="ma-card">
        <h2 className="ma-section-title" style={{ marginBottom: 10 }}>Minimum flat size</h2>
        <div className="ma-chips">
          {SIZES.map(size => (
            <Chip key={size} pressed={state.size === size} onClick={() => dispatch({ type: 'setSize', size })}>
              {size ? `${size} m²` : 'Any'}
            </Chip>
          ))}
        </div>
        <p className="ma-hint">We'll send flats this size or bigger.</p>
      </section>
      <section className="ma-card">
        <div className="ma-row">
          <h2 className="ma-section-title">Maximum deposit</h2>
          <span className="ma-facts">{state.deposit ? `Up to ${money(state.deposit)}` : 'Any'}</span>
        </div>
        <div className="ma-chips">
          {DEPOSITS.map(deposit => (
            <Chip
              key={deposit}
              pressed={state.deposit === deposit}
              onClick={() => dispatch({ type: 'setDeposit', deposit })}
            >
              {deposit ? `£${deposit / 1000}k` : 'Any'}
            </Chip>
          ))}
        </div>
        <p className="ma-hint">Ads that don't mention a deposit still come through.</p>
      </section>
    </div>
  );
}

function mustHaves(state: WizardState): string {
  const extras: string[] = [];
  if (!state.anyDate && state.date) extras.push('Move in ~' + state.date.split('-').reverse().join('/'));
  if (state.furnish.length) extras.push(FURNISH.filter(([o]) => state.furnish.includes(o)).map(([, l]) => l).join(' / '));
  if (state.bathMin > 1 || state.bathMax < MAX_BATHROOMS) extras.push(`${bathroomsRangeLabel(state.bathMin, state.bathMax)} bath`);
  if (state.size) extras.push(`${state.size} m²+`);
  if (state.deposit) extras.push(`Deposit up to ${money(state.deposit)}`);
  return extras.length ? extras.join(' · ') : 'None, you’ll see everything';
}

export function ReviewStep({ state, dispatch, locationNames }: StepProps & { locationNames: Map<string, string> }) {
  const where = state.mode === 'postcode'
    ? state.postcodes.join(', ')
    : state.areas.map(id => locationNames.get(id) ?? id).join(', ');
  const rows: { label: string; value: string; step: WizardStep }[] = [
    { label: 'Search by', value: state.mode === 'postcode' ? 'Postcode' : 'Neighbourhood', step: 'mode' },
    { label: 'Where', value: where || '—', step: 'where' },
    { label: 'Bedrooms', value: bedroomsRangeLabel(state.bedMin, state.bedMax), step: 'budget' },
    { label: 'Budget', value: `${budgetRangeLabel(state.budMin, state.budMax)} pcm`, step: 'budget' },
    { label: 'Must-haves', value: mustHaves(state), step: 'extras' },
  ];

  return (
    <div>
      <h1 className="ma-title">Check your search</h1>
      <p className="ma-subtitle">Tap any line to change it.</p>
      <div className="ma-card" style={{ paddingTop: 0, paddingBottom: 0 }}>
        {rows.map(row => (
          <div key={row.label} className="ma-review-row">
            <div>
              <div className="ma-review-row__label">{row.label}</div>
              <div className="ma-review-row__value">{row.value}</div>
            </div>
            <button
              type="button"
              className="ma-link-button"
              aria-label={`Edit ${row.label}`}
              onClick={() => dispatch({ type: 'edit', step: row.step })}
            >
              Edit
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
